/**
 * @description
 * Custom React hook for managing deployment data.
 * Provides methods for fetching and creating deployments.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 * @see admin/src/frontend/hooks/useDeployments.test.ts
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface Deployment {
  id: string;
  clientId: string;
  clientName: string;
  version: string;
  status: 'pending' | 'deploying' | 'success' | 'failed' | 'rolled_back';
  deployedBy: string;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  logs: string | null;
}

interface UseDeploymentsReturn {
  deployments: Deployment[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  createDeployment: (clientId: string, deployedBy: string, version?: string) => Promise<Deployment>;
}

/**
 * Hook for managing deployments
 */
export function useDeployments(clientId?: string): UseDeploymentsReturn {
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch deployments
   */
  const fetchDeployments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = clientId 
        ? await api.getClientDeployments(clientId)
        : await api.getDeployments();
      setDeployments(data);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching deployments:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  /**
   * Create a new deployment
   */
  const createDeployment = useCallback(async (
    clientId: string,
    deployedBy: string,
    version?: string
  ) => {
    const deployment = await api.createDeployment(clientId, { deployedBy, version });
    setDeployments(prev => [deployment, ...prev]);
    return deployment;
  }, []);

  // Fetch deployments on mount
  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  return {
    deployments,
    loading,
    error,
    refetch: fetchDeployments,
    createDeployment,
  };
}

/**
 * Hook for managing a single deployment
 */
export function useDeployment(id: string) {
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch deployment by ID
   */
  const fetchDeployment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getDeploymentStatus(id);
      setDeployment(data);
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching deployment:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch deployment on mount or when ID changes
  useEffect(() => {
    fetchDeployment();
  }, [fetchDeployment]);

  // Poll for updates if deployment is in progress
  useEffect(() => {
    if (deployment && (deployment.status === 'pending' || deployment.status === 'deploying')) {
      const interval = setInterval(() => {
        fetchDeployment();
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(interval);
    }
  }, [deployment, fetchDeployment]);

  return {
    deployment,
    loading,
    error,
    refetch: fetchDeployment,
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */